/**
 * Progress-Event tracker with offline queue + retry.
 *
 * Fire-and-forget API for callers (`trackEvent(...)` returns immediately).
 * Under the hood the tracker:
 *   1. Generates a UUID `client_event_id` per call (idempotency key —
 *      the server dedupes on re-send, so a retry never double-counts).
 *   2. Appends the event to an in-memory buffer AND persists the buffer
 *      to localStorage as-well (in case the browser tab crashes before
 *      the network round-trip completes).
 *   3. Fires POST /progress/events. On success the event is removed
 *      from the buffer.
 *   4. If the POST fails (offline, timeout, 5xx), the event stays in
 *      the buffer. On next `flush()` (called at app start + on `online`
 *      event + periodically) the whole buffer is bulk-sent to
 *      /progress/events/bulk. Server-side dedupe means the retry is
 *      safe even for events that partially succeeded.
 *
 * The buffer is capped so a long-offline session can't blow up
 * localStorage: MAX_QUEUE_SIZE=200 events. Older events are dropped
 * with a warning if the cap is hit (still 30+ minutes of active use).
 *
 * Design note: this tracker does NOT know about the domain (which POI
 * counts, which species matter). Callers pass raw event descriptors.
 * That keeps the tracker reusable for future event kinds without
 * touching this file.
 */

import {
  postProgressEvent,
  postProgressEventsBulk,
  type ProgressEventInput,
  type ProgressEventKind,
} from '../../api/progress'

const QUEUE_STORAGE_KEY = 'tschauko-progress-queue-v1'
const MAX_QUEUE_SIZE = 200

interface QueueEntry extends ProgressEventInput {
  /** How many times we've tried to send this event. Capped at
   *  RETRY_MAX below to avoid infinite loops on a permanently broken
   *  event (e.g. server rejects malformed payload). */
  attempts: number
}

const RETRY_MAX = 8

let queue: QueueEntry[] = loadQueue()
let flushInFlight = false

function loadQueue(): QueueEntry[] {
  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function persistQueue(): void {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue))
  } catch {
    /* quota — best-effort */
  }
}

function newClientEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for older browsers / insecure contexts (the app already
  // installs a randomUUID polyfill in index.html but this guards the
  // module in isolation).
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/** Track one event. Non-throwing. */
export function trackEvent(input: {
  kind: ProgressEventKind
  knowledge_id?: number | null
  waypoint_id?: number | null
  label?: string | null
  payload?: Record<string, unknown> | null
  /** Optional: pass a specific event-time (ISO). Defaults to now. */
  occurred_at?: string
}): void {
  const event: QueueEntry = {
    kind: input.kind,
    knowledge_id: input.knowledge_id ?? null,
    waypoint_id: input.waypoint_id ?? null,
    label: input.label ?? null,
    payload: input.payload ?? null,
    client_event_id: newClientEventId(),
    occurred_at: input.occurred_at ?? new Date().toISOString(),
    attempts: 0,
  }
  enqueue(event)
  void tryFireSingle(event)
}

function enqueue(event: QueueEntry): void {
  queue.push(event)
  if (queue.length > MAX_QUEUE_SIZE) {
    const dropped = queue.splice(0, queue.length - MAX_QUEUE_SIZE)
    console.warn(`[progress] queue cap ${MAX_QUEUE_SIZE} reached, dropped ${dropped.length} oldest events`)
  }
  persistQueue()
}

function removeFromQueue(clientEventId: string): void {
  const before = queue.length
  queue = queue.filter((e) => e.client_event_id !== clientEventId)
  if (queue.length !== before) persistQueue()
}

async function tryFireSingle(event: QueueEntry): Promise<void> {
  try {
    await postProgressEvent(event)
    removeFromQueue(event.client_event_id)
  } catch (e) {
    event.attempts += 1
    persistQueue()
    console.debug('[progress] event send failed, queued for retry:', e)
    // Piggyback: on failure schedule a full flush shortly. If we're
    // offline the flush also fails, then the `online` event handler
    // below picks it up when we come back.
    scheduleDelayedFlush()
  }
}

let delayedFlushTimer: number | null = null

function scheduleDelayedFlush(): void {
  if (delayedFlushTimer != null) return
  delayedFlushTimer = window.setTimeout(() => {
    delayedFlushTimer = null
    void flushQueue()
  }, 5000)
}

/** Send all queued events in one bulk POST. Called at app start, on
 *  `online` transition, and after per-event failures. Idempotent
 *  server-side, safe to call any time. */
export async function flushQueue(): Promise<void> {
  if (flushInFlight) return
  if (queue.length === 0) return
  flushInFlight = true
  const batch = queue.filter((e) => e.attempts < RETRY_MAX).slice()
  const droppedForRetryCap = queue.length - batch.length
  if (droppedForRetryCap > 0) {
    console.warn(`[progress] ${droppedForRetryCap} events exceed retry cap, dropping`)
    queue = queue.filter((e) => e.attempts < RETRY_MAX)
    persistQueue()
  }
  if (batch.length === 0) {
    flushInFlight = false
    return
  }
  try {
    await postProgressEventsBulk(batch)
    // Success — remove all sent events from the queue. Even if some
    // were deduped server-side (already-known client_event_ids), we
    // want them gone from our queue.
    const sentIds = new Set(batch.map((e) => e.client_event_id))
    queue = queue.filter((e) => !sentIds.has(e.client_event_id))
    persistQueue()
    console.log(`[progress] flushed ${batch.length} events`)
  } catch (e) {
    // Bump attempt-count on the batch we tried; leave them in the
    // queue for the next flush.
    for (const q of queue) {
      if (batch.some((b) => b.client_event_id === q.client_event_id)) {
        q.attempts += 1
      }
    }
    persistQueue()
    console.debug('[progress] bulk flush failed:', e)
  } finally {
    flushInFlight = false
  }
}

/** Install the auto-flush triggers: on `online` event + on tab visible.
 *  Call once at app start (from TschaukoAgentRoot on mount). */
export function installFlushTriggers(): void {
  window.addEventListener('online', () => {
    console.debug('[progress] network back — flushing queue')
    void flushQueue()
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushQueue()
  })
  // Kick off an initial flush shortly after mount in case there are
  // events left over from a previous session.
  window.setTimeout(() => void flushQueue(), 2000)
}

/** Diagnostics. Not used by app code, useful for dev inspection. */
export function getQueueSize(): number {
  return queue.length
}
